import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useToast } from "../../context/ToastContext";
import LineApi, { LineRequest } from "../../services/LineApi";
import AreaApi from "../../services/AreaApi";
import ProductionUnitApi from "../../services/ProductionUnitApi";
import { useAuth } from "../../context/AuthContext";

// Design System
import FormCard from "../../components/FormCard";
import Input from "../../components/Input/Input";
import ConfirmModal from "../../components/ConfirmModal/ConfirmModal";
import { classNames } from "primereact/utils";

const CreateLineForm = (): JSX.Element => {
  const { plantId } = useAuth();
  const navigate = useNavigate();
  const { displayToast } = useToast();
  const { t: translateForm } = useTranslation("form");
  const { t: translateMessage } = useTranslation("messages");

  const nameRef = useRef<any>(null);
  const areaRef = useRef<any>(null);
  const unitRef = useRef<any>(null);

  const [name, setName] = useState<string>("");
  // Production Unit and Area hierarchical selectors
  const [unitOptions, setUnitOptions] = useState<{ id: string; name: string }[]>([]);
  const [unitId, setUnitId] = useState<string | null>(null);
  const [loadingUnits, setLoadingUnits] = useState<boolean>(false);
  
  const areasRequestIdRef = useRef<number>(0);
  const [areaOptions, setAreaOptions] = useState<{ id: string; name: string }[]>([]);
  const [areaId, setAreaId] = useState<string | null>(null);
  const [loadingAreas, setLoadingAreas] = useState<boolean>(false);
  const [showConfirmCancel, setShowConfirmCancel] = useState(false);

  const [lines, setLines] = useState<any[]>([]);

  // Carrega unidades de produÃ§Ã£o
  const loadUnits: any = async () => {
    if (!plantId) return;
    setLoadingUnits(true);
    try {
      const res = await ProductionUnitApi.GetProductUnitByPlant(plantId);
      const opts = (res.data as any[]).map(u => ({ id: u.id, name: u.name ?? u.nome }));
      setUnitOptions(opts);
      if (opts.length === 1) setUnitId(opts[0].id);
    } catch (err) {
      console.error("Erro ao carregar unidades:", err);
      displayToast("error", translateMessage("error"), "", false, 3000);
    } finally {
      setLoadingUnits(false);
    }
  };

  // Carrega todas as linhas para checar duplicidade de nome
  const fetchAllLines = async (): Promise<void> => {
    try {
      if (!plantId) {
        setLines([]);
        return;
      }
      const resp = await LineApi.GetLinesByPlant(plantId);
      setLines(resp.data);
    } catch (err) {
      console.error("Erro ao buscar linhas:", err);
      setLines([]);
    }
  };

  useEffect(() => {
    loadUnits();
    fetchAllLines();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plantId]);

  // Carrega Ã¡reas quando a unidade muda
  useEffect(() => {
    if (!unitId) {
      setAreaOptions([]);
      setAreaId(null);
      return;
    }
    setLoadingAreas(true);
    setAreaOptions([]);
    setAreaId(null);
    const rid = ++areasRequestIdRef.current;
    
    AreaApi.GetAreaByPlantIdStatic(plantId!)
      .then((res: any) => {
        if (rid !== areasRequestIdRef.current) return;
        const all = res.data as any[];
        const filtered = all.filter(a => a.productionUnitId === unitId || a.productionUnit?.id === unitId);
        const opts = filtered.map(a => ({ id: a.id, name: a.name ?? a.nome }));
        setAreaOptions(opts);
        if (opts.length === 1) setAreaId(opts[0].id);
      })
      .catch((err: any) => {
        if (rid !== areasRequestIdRef.current) return;
        console.error("Erro ao buscar Ã¡reas:", err);
        displayToast("error", translateMessage("error"), "", false, 3000);
        setAreaOptions([]);
      })
      .finally(() => {
        if (rid === areasRequestIdRef.current) setLoadingAreas(false);
      });
  }, [unitId, plantId]);

  const handleCancel = (): void => {
    setShowConfirmCancel(true);
  };

  const handleSave = async (e?: React.FormEvent): Promise<void> => {
    e?.preventDefault();
    const trimmed = name?.trim() ?? "";
    
    if (!trimmed || !unitId || !areaId) {
      displayToast(
        "warn",
        translateForm("warning"),
        translateForm("fillInAllRequiredFields"),
      );
      if (!unitId) unitRef.current?.focus();
      else if (!areaId) areaRef.current?.focus();
      else nameRef.current?.focus();
      return;
    }

    // Verifica duplicidade de nome
    const nameExists = lines.some(
      (ln) => ln.name && String(ln.name).toLowerCase() === trimmed.toLowerCase(),
    );

    if (nameExists) {
      displayToast(
        "error",
        translateMessage("error"),
        translateMessage("duplicateLineError"),
        false,
        3000,
      );
      return;
    }

    const payload: LineRequest = {
      id: undefined,
      name: trimmed,
      areaId: areaId ?? undefined,
    };

    try {
      await LineApi.Create(payload);
      displayToast(
        "success",
        translateForm("Success"),
        translateMessage("registerSuccess"),
        false,
        3000,
      );
      navigate("/lines");
    } catch (err: any) {
      const msg =
        err?.response?.data?.message ??
        err?.response?.data ??
        err?.message ??
        translateMessage("error");
      displayToast("error", translateMessage("error"), String(msg), false, 4000);
    }
  };

  return (
    <>
      <ConfirmModal
        visible={showConfirmCancel}
        onHide={() => setShowConfirmCancel(false)}
        onConfirm={() => navigate("/lines")}
      />
      <FormCard
        title={translateForm("registerLine")}
        icon="precision_manufacturing"
        infoIcon="info"
        infoText="Preencha os dados primÃ¡rios da linha de produÃ§Ã£o."
        submitButtonLabel={translateForm("register")}
        cancelButtonLabel={translateForm("Cancel")}
        onSubmit={handleSave}
        onCancel={handleCancel}
        numColumns={6}
      >
        {/* Unidade de ProduÃ§Ã£o */}
        <div style={{ gridColumn: "span 3" }}>
          <Input
            ref={unitRef}
            type="select"
            label={translateForm("productionUnit")}
            required={true}
            value={unitId}
            options={unitOptions}
            optionLabel="name"
            optionValue="id"
            placeholder={translateForm("selectUnit")}
            className={classNames("w-full", { "p-invalid border-red": !unitId })}
            disabled={loadingUnits}
            onChange={(val: any) => setUnitId(val)}
          />
        </div>

        {/* Ãrea */}
        <div style={{ gridColumn: "span 3" }}>
          <Input
            ref={areaRef}
            type="select"
            label={translateForm("area")}
            required={true}
            value={areaId}
            options={areaOptions}
            optionLabel="name"
            optionValue="id"
            placeholder={translateForm("selectArea")}
            className={classNames("w-full", { "p-invalid border-red": !areaId })}
            disabled={!unitId || loadingAreas}
            onChange={(val: any) => setAreaId(val)}
          />
        </div>

        {/* Nome */}
        <div style={{ gridColumn: "span 6" }}>
          <Input
            ref={nameRef}
            type="text"
            label={translateForm("name")}
            required={true}
            value={name}
            onChange={(val: any) => setName(val)}
            placeholder={translateForm("name")}
            className={classNames("w-full", { "p-invalid border-red": !name.trim() })}
          />
        </div>
      </FormCard>
    </>
  );
};

export default CreateLineForm;
